import { isBefore } from "date-fns";
import { DEMOGRAPHIC_GROUP_IDS, DEMOGRAPHIC_IDS } from "../types/report";
import { ScaleValue } from "../types/scale";
import {
  BENCHMARKING_SCORE_002,
  BENCHMARKING_SCORE_003,
  BENCHMARKING_SCORE_004,
  DemographicGroup,
  GetDemographicGroupsResponse,
  GetSurveyScoringDemographicResponse,
  GetSurveyScoringResponse,
  SurveyScoringDemographic,
  SurveyScoringDemographicGroup,
} from "../types/workplaceCulture";

export interface ScoreFilter {
  scaleValues: ScaleValue[];
  averageValue: ScaleValue;
  info: string;
  missingDemographics: boolean;
}

export interface GroupedSurveyDemographicScopes {
  [key: string]: SurveyScoringDemographic[];
}

export const OVERVIEW_FILTER_GROUPS_COUNT = 6;
export const STEP_SIZE = 0.1;
export const MIN_SCALE_VALUE = 0;
export const MAX_SCALE_VALUE = 5;
export const OVERVIEW_DEMOGRAPHIC_GROUP_ID = "allResults";
// minimum engagement threshold to get score
export const ENGAGEMENT_THRESHOLD = 30;

// minimum  scoring and breakdown threshold
export const DEMOGRAPHIC_THRESHOLD = 15;

export const getOrganizationDemographic = (
  demographicsData?: GetDemographicGroupsResponse
) => {
  return demographicsData?.demographic_groups
    ?.find((group) => group.id === DEMOGRAPHIC_GROUP_IDS.ORGANISATION)
    ?.demographics?.find(
      (demographic) => demographic.id === DEMOGRAPHIC_IDS.ORGANISATION
    );
};

export const generateVisibleScores = (
  data: GetSurveyScoringResponse,
  minScoreValue: number,
  maxScoreValue: number,
  selectedFilterIndexes: string[],
  selectedFilterId: string | undefined,
  isChapterScore: boolean,
  hideIsSkip: boolean,
  t: (str: string, options?: any) => string,
  title?: string
): ScoreFilter => {
  const averageValue = {
    label: isChapterScore
      ? t("scale.filters.average.chapterTitle", {
          chapterName: title,
        })
      : t("scale.filters.average.title"),
    score: Math.abs(data.total_score || 0),
  };

  const filtersWithoutSelectedFilter = data.demographic_groups.filter(
    (filter) =>
      filter.demographics.every((group) => group.id !== selectedFilterId)
  );

  if (selectedFilterIndexes.includes(OVERVIEW_DEMOGRAPHIC_GROUP_ID)) {
    const overviewFilterValues = getOverviewFilterValues(
      filtersWithoutSelectedFilter,
      hideIsSkip,
      t
    );

    const overviewFilter: ScoreFilter = {
      scaleValues: overviewFilterValues,
      averageValue: averageValue,
      info: t("scale.filters.overview.info"),
      missingDemographics:
        overviewFilterValues.length < OVERVIEW_FILTER_GROUPS_COUNT,
    };
    // remove scale values from overview filter that are out of range
    overviewFilter.scaleValues = overviewFilter.scaleValues.filter(
      (scaleValue) =>
        scaleValue.score >= minScoreValue && scaleValue.score <= maxScoreValue
    );

    return overviewFilter;
  }

  // combine selected filters into one array
  const selectedFilters: SurveyScoringDemographicGroup[] =
    filtersWithoutSelectedFilter.filter((filter) =>
      selectedFilterIndexes.includes(filter.id)
    );

  // get all scale values that are in the selected filters
  const allGroupValues = selectedFilters.flatMap((filter) =>
    filter.demographics.filter(
      (group) => !!group.score && (hideIsSkip ? !group.is_skip : true)
    )
  );

  const allScaleValues = transformScoringGroupsToScaleValue(
    allGroupValues,
    minScoreValue,
    maxScoreValue,
    t
  );

  // check if any of selectedFilters has missing demographics
  let anyMissingDemographics = false;
  selectedFilters.forEach((filter) => {
    anyMissingDemographics =
      anyMissingDemographics || hasMissingDemographics(filter.demographics);
  });

  return {
    scaleValues: allScaleValues,
    averageValue: averageValue,
    info: t(
      `scale.filters.${data.demographic_groups[1].label.toLowerCase()}.info`,
      ""
    ),
    missingDemographics: anyMissingDemographics,
  };
};

export const getGroupedDemographicScopes = (
  scoringData: GetSurveyScoringResponse
): GroupedSurveyDemographicScopes => {
  const filters: GroupedSurveyDemographicScopes = {};

  scoringData.demographic_groups.forEach((filter) => {
    // filter out groups that are not is_scope
    const scopedGroups = filter.demographics.filter(
      (demographic) => demographic.is_scope
    );
    if (scopedGroups.length > 0) {
      filters[filter.label] = scopedGroups;
    }
  });

  return filters;
};

export const getOverviewFilterValues = (
  surveyScores: DemographicGroup<SurveyScoringDemographic>[],
  hideIsSkip: boolean,
  t: (str: string, options?: any) => string
): ScaleValue[] => {
  const surveyScoresFlat = surveyScores
    .flatMap((scoringGroup) => scoringGroup.demographics)
    .filter((scoringGroup) => !!scoringGroup?.score)
    .filter((scoringGroup) => (hideIsSkip ? !scoringGroup.is_skip : true))
    .sort((valueA, valueB) => (valueA?.score || 0) - (valueB?.score || 0));

  if (surveyScoresFlat.length < OVERVIEW_FILTER_GROUPS_COUNT) {
    const values: ScaleValue[] = [];

    surveyScoresFlat.map((scoreGroup) =>
      values.push({
        label: t(`scale.groups.${scoreGroup.label}`, scoreGroup.label),
        score: Math.abs(scoreGroup.score || 0),
      })
    );

    return values;
  }

  // Note: If more than 3 groups that have the lowest or highest score,
  // the one that first appear are chosen to appear.
  return [
    {
      label: t(
        `scale.groups.${surveyScoresFlat[0].label}`,
        surveyScoresFlat[0].label
      ),
      score: Math.abs(surveyScoresFlat[0].score || 0),
    },
    {
      label: t(
        `scale.groups.${surveyScoresFlat[1].label}`,
        surveyScoresFlat[1].label
      ),
      score: Math.abs(surveyScoresFlat[1].score || 0),
    },
    {
      label: t(
        `scale.groups.${surveyScoresFlat[2].label}`,
        surveyScoresFlat[2].label
      ),
      score: Math.abs(surveyScoresFlat[2].score || 0),
    },
    {
      label: t(
        `scale.groups.${surveyScoresFlat[surveyScoresFlat.length - 3].label}`,
        surveyScoresFlat[surveyScoresFlat.length - 3].label
      ),
      score: Math.abs(surveyScoresFlat[surveyScoresFlat.length - 3].score || 0),
    },
    {
      label: t(
        `scale.groups.${surveyScoresFlat[surveyScoresFlat.length - 2].label}`,
        surveyScoresFlat[surveyScoresFlat.length - 2].label
      ),
      score: Math.abs(surveyScoresFlat[surveyScoresFlat.length - 2].score || 0),
    },
    {
      label: t(
        `scale.groups.${surveyScoresFlat[surveyScoresFlat.length - 1].label}`,
        surveyScoresFlat[surveyScoresFlat.length - 1].label
      ),
      score: Math.abs(surveyScoresFlat[surveyScoresFlat.length - 1].score || 0),
    },
  ];
};

export const transformScoringGroupsToScaleValue = (
  surveyScoringDemographics: SurveyScoringDemographic[],
  minScoreValue: number,
  maxScoreValue: number,
  t: (str: string, options?: any) => string
): ScaleValue[] => {
  return (
    surveyScoringDemographics
      .filter((demographic) => !!demographic.score)
      // filter values out of range
      .filter(
        (demographic) =>
          (demographic.score || MIN_SCALE_VALUE - 1) >= minScoreValue &&
          (demographic.score || MAX_SCALE_VALUE + 1) <= maxScoreValue
      )
      .map((demographic) => {
        return {
          label: t(`scale.groups.${demographic.label}`, demographic.label),
          score: demographic.score || MIN_SCALE_VALUE,
        };
      })
      .sort((a, b) => a.score - b.score)
  );
};

const hasMissingDemographics = (
  surveyScoringDemographics: SurveyScoringDemographic[]
) => {
  return (
    surveyScoringDemographics.filter((demographic) => !!demographic.score)
      .length !== surveyScoringDemographics.length
  );
};

// Returns the minimum and maximum score values from the survey scoring data
export const getMinMaxScoreValuesForFilter = (filterData: ScoreFilter) => {
  const minScoreValue = Math.min(
    ...filterData.scaleValues.map((group) => group.score || MIN_SCALE_VALUE)
  );

  const maxScoreValue = Math.max(
    ...filterData.scaleValues.map((group) => group.score || MAX_SCALE_VALUE)
  );

  return {
    minScoreValue: Math.floor(minScoreValue),
    maxScoreValue: Math.ceil(maxScoreValue),
  };
};

export const createSegmentPieces = (
  minValue: number,
  maxValue: number,
  small: boolean
) => {
  const stepSize = small ? 1 : STEP_SIZE;
  const results: number[] = [];
  let currentValue = minValue + stepSize;
  while (currentValue <= maxValue) {
    results.push(parseFloat(currentValue.toFixed(1)));
    currentValue += stepSize;
  }

  // Check if maxValue should be included exactly, avoiding floating point precision issues
  if (
    parseFloat(maxValue.toFixed(1)) === maxValue &&
    !results.includes(maxValue)
  ) {
    results.push(maxValue);
  }
  return results;
};

export const isSurveyClosed = (dueDate: Date | string | undefined | null) => {
  if (!dueDate) {
    return false;
  }

  const dueDateObj = new Date(dueDate);
  const now = new Date();

  return isBefore(dueDateObj, now);
};

export const surveyHasAnswers = (
  scoringData: GetSurveyScoringDemographicResponse | undefined
): boolean => {
  return scoringData?.scores.total_score !== null;
};

export const getDemographicTotalScore = (
  scoringData: GetSurveyScoringDemographicResponse | undefined
) => {
  return parseFloat(scoringData?.scores?.total_score?.toFixed(2) || "0.0");
};

// TODO: Replace with actual benchmarking scores from API when available
export const getChapterBenchmarkScore = (chapterId: string): number => {
  const chapterIdNumber = chapterId.split(".")[2];

  // TODO: Replace with actual benchmarking scores from API when available
  let benchmarkScore = 0;
  if (chapterIdNumber === "002") benchmarkScore = BENCHMARKING_SCORE_002;
  else if (chapterIdNumber === "003") benchmarkScore = BENCHMARKING_SCORE_003;
  else if (chapterIdNumber === "004") benchmarkScore = BENCHMARKING_SCORE_004;

  return benchmarkScore;
};
